import { Component, OnInit } from '@angular/core';
import { User } from '../../../app/models/user';
import { Organization, OrganizationCourse } from '../../../app/models/organization';
import { UserService } from '../../../app/lib/user.service';
import { ApiService } from '../../../app/lib/api.service';
import { MenuService } from 'app/lib/menu.service';
import { TranslateService } from '@ngx-translate/core';

interface UserBadge {
    id: string;
    name: string;
    earnedOn: string;
    imagePath: string;
}

@Component({
    selector: 'page-badges',
    templateUrl: 'badges.html',
    styleUrls: ['./badges.scss'],
    standalone: false
})
export class BadgesPage implements OnInit {
    user: User;
    organization: Organization;
    badges: UserBadge[] = [];
    isLoading: boolean = true;
    courses: OrganizationCourse[] = [];
    userName: string = '';
    
    constructor(
        private userService: UserService,
        private api: ApiService,
        private menuservice: MenuService,
        private translateService: TranslateService
    ) {
        this.user = this.userService.getUser();
        this.userName = this.user?.fullName || '';
    }

    ngOnInit() {
        this.loadUserBadges();
        this.updateBadgesViewTime();
    }

    updateBadgesViewTime() {
        this.api.post(`user/badges/view`, {
            user_id: this.user?.id
        }).subscribe(() => {
            this.menuservice.updateNewBadgesCount(0);
        });
    }

    loadUserBadges() {
        this.isLoading = true;
        const lang = this.translateService.currentLang || 'en';
        const userId = this.user?.id;
        this.api.get(`user/badges?user_id=${userId}`).subscribe(
            (result: any) => {
                if (result) {
                    this.badges = [];
                    result.badges.forEach(badge => {
                        this.badges.push({
                            id: badge.BadgeKey,
                            name: badge.Name,
                            earnedOn: badge.EarnedOnUtc || badge.CreatedOnUtc,
                            imagePath: `assets/img/badges/${badge.BadgeKey.toLowerCase()}_${lang}.png`
                        });
                    });
                } else {
                    this.badges = [];
                }
                this.isLoading = false;
            },
            (error: any) => {
                this.badges = [];
                this.isLoading = false;
            }
        );
    }

    /**
     * Download badge image when user clicks the download icon
     * Now generates a new image with badge, user name, and earned date.
     */
    async downloadBadge(badge: any, event: MouseEvent) {
        // Prevent event from bubbling up
        event.stopPropagation();

        // Format the earned date as MM-dd-yyyy
        let earnedDate = '';
        if (badge.earnedOn) {
            const date = new Date(badge.earnedOn);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            earnedDate = `${month}-${day}-${year}`;
        }

        // Sanitize user name for filename — strip everything except [a-zA-Z0-9_-]
        // so the value cannot escape its intended use as a download attribute.
        const sanitize = (s: string) =>
            (s || '').replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 60);
        const userName = sanitize(this.userName) || 'user';
        const badgeName = sanitize(badge.name) || 'badge';
        const fileName = `${badgeName}_${userName}${earnedDate ? '_' + earnedDate : ''}.png`;

        // Create a canvas to draw the badge image, user name, and earned date
        const canvasSize = 320;
        const canvas = document.createElement('canvas');
        canvas.width = canvasSize;
        canvas.height = canvasSize + 60; // Extra space for text
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        // Draw white background
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Load the badge image
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.src = badge.imagePath;
        img.onload = () => {
            // Draw the badge image centered
            const imgSize = 300;
            ctx.drawImage(img, (canvasSize - imgSize) / 2, 10, imgSize, imgSize);

            // Draw user name
            ctx.font = 'bold 20px Arial';
            ctx.fillStyle = '#4B17A7';
            ctx.textAlign = 'center';
            ctx.fillText(this.userName || '', canvasSize / 2, canvasSize + 30);

            // Draw earned date
            ctx.font = '16px Arial';
            ctx.fillStyle = '#6f747d';
            ctx.fillText(earnedDate, canvasSize / 2, canvasSize + 55);

            // Download the canvas as an image. Detached anchors with a
            // data: URL trigger a download via .click() in all modern
            // browsers — no need to attach to the DOM.
            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = fileName;
            link.click();
        };
        img.onerror = () => {};
    }

    handleImageError(badge: any) {}
}
